import { useState, useRef, RefObject, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Stack, Popover } from '@mui/material';

import {
  application_overview_path,
  organization_path,
  project_overview_path,
  useTypedParams,
  organization_projects_path,
  project_application_list_path,
} from 'app-zephyr-routes';
import { SlashIcon } from 'app-zephyr-icons';
import {
  generateOrganizationPathParams,
  generateProjectPathParams,
  generateApplicationPathParams,
} from 'app-zephyr-utils';
import { CreateNewBtn } from 'app-zephyr-components/create-new-btn';
import {
  useNavigationOrgItemList,
  useNavigationProjItemList,
  useNavigationAppItemList,
  usePinItem,
  PinItemProps,
  useUnPinItem,
} from 'app-zephyr-domains/user';
import { NavigationItem, Error as ResponseError } from 'ze-api-contract/user-v2/navigation-items';

import { Loading } from './loading';
import { TopFilter } from './top-filter';
import { BreadcrumbDropdownItem } from './breadcrumb-dropdown-item';
import { BreadcrumbItem } from './breadcrumb-item';
import { BreadcrumbType } from './interfaces';
import { useStyles } from './styles';
import { SnackBarAlertComponent } from '../../../SnackBarAlert';

const DropdownTypeWidth = {
  [BreadcrumbType.ORGANIZATION]: 855,
  [BreadcrumbType.PROJECT]: 570,
  [BreadcrumbType.APPLICATION]: 285,
};

interface SearchQueryValues {
  [BreadcrumbType.PROJECT]: string;
  [BreadcrumbType.APPLICATION]: string;
  [BreadcrumbType.ORGANIZATION]: string;
}

const DROPDOWN_ID = 'breadcrumbs-dropdown';

export function BreadCrumbs() {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { organization, project, application } = useTypedParams();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [clickedType, setClickedType] = useState<BreadcrumbType | null>(null);
  const [selectedValues, setSelectedValues] = useState<SearchQueryValues>({
    [BreadcrumbType.PROJECT]: project ?? '',
    [BreadcrumbType.APPLICATION]: application ?? '',
    [BreadcrumbType.ORGANIZATION]: organization ?? '',
  });
  const [searchValues, setSearchValues] = useState<SearchQueryValues>({
    [BreadcrumbType.PROJECT]: '',
    [BreadcrumbType.APPLICATION]: '',
    [BreadcrumbType.ORGANIZATION]: '',
  });
  const [snackBarMessage, setSnackBarMessage] = useState<string | null>(null);

  const { organizationList, isLoading: isLoadingOrgList } = useNavigationOrgItemList({
    name: searchValues[BreadcrumbType.ORGANIZATION],
  });
  const { projectList, isLoading: projectListLoading } = useNavigationProjItemList(
    selectedValues[BreadcrumbType.ORGANIZATION],
    { name: searchValues[BreadcrumbType.PROJECT] },
  );
  const { applicationList, isLoading: applicationListLoading } = useNavigationAppItemList(
    selectedValues[BreadcrumbType.ORGANIZATION],
    selectedValues[BreadcrumbType.PROJECT],
    { name: searchValues[BreadcrumbType.APPLICATION] },
  );
  const { mutateAsync: pinNavigationItem } = usePinItem();
  const { mutateAsync: unPinNavigationItem } = useUnPinItem();

  useEffect(() => {
    setSelectedValues({
      [BreadcrumbType.PROJECT]: project ?? '',
      [BreadcrumbType.APPLICATION]: application ?? '',
      [BreadcrumbType.ORGANIZATION]: organization ?? '',
    });
  }, [organization, project, application]);

  const orgRef = useRef<HTMLDivElement>(null);
  const projRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<HTMLDivElement>(null);

  const handleClick = (elem: RefObject<HTMLDivElement | null>, type: BreadcrumbType) => {
    setClickedType(type);
    setAnchorEl(elem.current ?? null);
    setOpen((previousOpen) => !previousOpen);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setOpen(false);
    setSearchValues({
      organization: '',
      project: '',
      application: '',
    });
  };

  const handleNavigateClick = (path: string) => {
    void navigate({ to: path, search: (p: never) => p, params: (p: never) => p });
    handleCloseDropdown();
  };

  const getPinBodyRequest = (id: string, type: BreadcrumbType): PinItemProps => {
    if (type === BreadcrumbType.ORGANIZATION) {
      return { type, organization: { id, queryNameParam: searchValues[BreadcrumbType.ORGANIZATION] } };
    }

    if (type === BreadcrumbType.PROJECT) {
      return {
        type,
        organization: { name: selectedValues[BreadcrumbType.ORGANIZATION] },
        project: { id, queryNameParam: searchValues[BreadcrumbType.PROJECT] },
      };
    }

    return {
      type,
      organization: { name: selectedValues[BreadcrumbType.ORGANIZATION] },
      project: { name: selectedValues[BreadcrumbType.PROJECT] },
      application: { id, queryNameParam: searchValues[BreadcrumbType.APPLICATION] },
    };
  };

  const handlePinClick = async (id: string, type: BreadcrumbType, isPinned: boolean) => {
    const bodyRequest = getPinBodyRequest(id, type);
    if (!isPinned) {
      await pinNavigationItem(bodyRequest).catch((error: unknown) => {
        setSnackBarMessage(
          (error as ResponseError).response?.data?.errorMessage ?? 'Something went wrong. Please, try again.',
        );
      });
      return;
    }

    await unPinNavigationItem(bodyRequest).catch((error: unknown) => {
      setSnackBarMessage(
        (error as ResponseError).response?.data?.errorMessage ?? 'Something went wrong. Please, try again.',
      );
    });
  };

  return (
    <>
      <div className={classes.breadcrumbContent} aria-describedby={DROPDOWN_ID}>
        <Stack ref={orgRef} display={'flex'} flexDirection={'row'}>
          <SlashIcon />
          <BreadcrumbItem
            type={BreadcrumbType.ORGANIZATION}
            isPrevious={{ organization: !!project }}
            value={{ organization }}
            isActive={{ organization: !!(organization && !project) }}
            onClicked={() => {
              handleClick(orgRef, BreadcrumbType.ORGANIZATION);
            }}
            counter={{ organization: getCounterNumber(!!organization, organizationList ?? []) }}
            navigateClick={() => {
              const url = organization_path(generateOrganizationPathParams(organization ?? ''));
              handleNavigateClick(url);
            }}
          />
        </Stack>
        <Stack ref={projRef} display={'flex'} flexDirection={'row'}>
          <SlashIcon />
          <BreadcrumbItem
            type={BreadcrumbType.PROJECT}
            value={{ project }}
            isPrevious={{ project: !!application }}
            isActive={{ project: !!(project && !application) }}
            onClicked={() => {
              handleClick(projRef, BreadcrumbType.PROJECT);
            }}
            navigateClick={() => {
              const url = project_overview_path(generateProjectPathParams(organization ?? '', project ?? ''));
              handleNavigateClick(url);
            }}
            counter={{ project: getCounterNumber(!!project, projectList ? projectList : []) }}
          />
        </Stack>
        <Stack ref={appRef} display={'flex'} flexDirection={'row'}>
          <SlashIcon />
          <BreadcrumbItem
            type={BreadcrumbType.APPLICATION}
            value={{ application }}
            isPrevious={{ application: false }}
            isActive={{ application: !!application }}
            onClicked={() => {
              handleClick(appRef, BreadcrumbType.APPLICATION);
            }}
            counter={{ application: getCounterNumber(!!application, applicationList ? applicationList : []) }}
          />
        </Stack>
      </div>

      <Popover
        id={DROPDOWN_ID}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={classes.breadcrumbsPopover}
      >
        <Stack
          data-e2e="breadcrumbs-dropdown"
          width={clickedType ? DropdownTypeWidth[clickedType] : 0}
          className={classes.dropdownBox}
        >
          <Stack>
            <Stack flexDirection={'row'}>
              {clickedType === BreadcrumbType.ORGANIZATION && (
                <Stack className={classes.dropdownItem}>
                  <TopFilter
                    name={searchValues[BreadcrumbType.ORGANIZATION]}
                    filterIsChanged={(value: string) => {
                      setSearchValues({ ...searchValues, [BreadcrumbType.ORGANIZATION]: value });
                    }}
                    placeholder="Find Organization"
                  />
                  {isLoadingOrgList && <Loading />}
                  {!isLoadingOrgList && (
                    <BreadcrumbDropdownItem
                      itemList={organizationList ?? []}
                      type={BreadcrumbType.ORGANIZATION}
                      selectedItem={selectedValues[BreadcrumbType.ORGANIZATION]}
                      onNavigationClick={(name: string) => {
                        handleNavigateClick(organization_path(generateOrganizationPathParams(name)));
                      }}
                      onPinClick={(id: string, isPinned: boolean) => {
                        void handlePinClick(id, BreadcrumbType.ORGANIZATION, isPinned);
                      }}
                      onSelectClick={(name: string) => {
                        setSelectedValues({
                          [BreadcrumbType.ORGANIZATION]: name,
                          [BreadcrumbType.PROJECT]: '',
                          [BreadcrumbType.APPLICATION]: '',
                        });
                      }}
                      onViewAllClick={() => {
                        handleNavigateClick('/');
                      }}
                    />
                  )}
                </Stack>
              )}
              {(clickedType === BreadcrumbType.ORGANIZATION || clickedType === BreadcrumbType.PROJECT) && (
                <Stack className={classes.dropdownItem}>
                  <TopFilter
                    name={searchValues[BreadcrumbType.PROJECT]}
                    filterIsChanged={(value: string) => {
                      setSearchValues({ ...searchValues, [BreadcrumbType.PROJECT]: value });
                    }}
                    placeholder="Find Project"
                  />
                  {projectListLoading && <Loading />}
                  {!projectListLoading && (
                    <BreadcrumbDropdownItem
                      itemList={projectList ? projectList : []}
                      type={BreadcrumbType.PROJECT}
                      selectedItem={selectedValues[BreadcrumbType.PROJECT]}
                      onNavigationClick={(name: string) => {
                        handleNavigateClick(
                          project_overview_path(
                            generateProjectPathParams(selectedValues[BreadcrumbType.ORGANIZATION], name),
                          ),
                        );
                      }}
                      onPinClick={(id: string, isPinned: boolean) => {
                        void handlePinClick(id, BreadcrumbType.PROJECT, isPinned);
                      }}
                      onSelectClick={(name: string) => {
                        setSelectedValues({
                          ...selectedValues,
                          [BreadcrumbType.PROJECT]: name,
                          [BreadcrumbType.APPLICATION]: '',
                        });
                      }}
                      onViewAllClick={() => {
                        handleNavigateClick(
                          organization_projects_path(
                            generateOrganizationPathParams(selectedValues[BreadcrumbType.ORGANIZATION]),
                          ),
                        );
                      }}
                    />
                  )}
                </Stack>
              )}
              <Stack className={classes.dropdownItem}>
                <TopFilter
                  name={searchValues[BreadcrumbType.APPLICATION]}
                  filterIsChanged={(value: string) => {
                    setSearchValues({ ...searchValues, [BreadcrumbType.APPLICATION]: value });
                  }}
                  placeholder="Find Application"
                />
                {applicationListLoading && <Loading />}
                {!applicationListLoading && (
                  <BreadcrumbDropdownItem
                    itemList={applicationList ? applicationList : []}
                    type={BreadcrumbType.APPLICATION}
                    selectedItem={selectedValues[BreadcrumbType.APPLICATION]}
                    onNavigationClick={(name: string) => {
                      handleNavigateClick(
                        application_overview_path(
                          generateApplicationPathParams(
                            selectedValues[BreadcrumbType.ORGANIZATION],
                            selectedValues[BreadcrumbType.PROJECT],
                            name,
                          ),
                        ),
                      );
                    }}
                    onPinClick={(id: string, isPinned: boolean) => {
                      void handlePinClick(id, BreadcrumbType.APPLICATION, isPinned);
                    }}
                    onSelectClick={(name: string) => {
                      setSelectedValues({ ...selectedValues, [BreadcrumbType.APPLICATION]: name });
                    }}
                    onViewAllClick={() => {
                      handleNavigateClick(
                        project_application_list_path(
                          generateProjectPathParams(
                            selectedValues[BreadcrumbType.ORGANIZATION],
                            selectedValues[BreadcrumbType.PROJECT],
                          ),
                        ),
                      );
                    }}
                  />
                )}
              </Stack>
            </Stack>
            {/* todo: should be added onBtnClick={handleCloseDropdown}. For now it conflicts */}
            <CreateNewBtn
              navigateStarted={() => {
                setTimeout(() => {
                  handleCloseDropdown();
                }, 10);
              }}
            />
          </Stack>
        </Stack>
      </Popover>

      <SnackBarAlertComponent
        open={!!snackBarMessage}
        onClose={() => {
          setSnackBarMessage(null);
        }}
        message={snackBarMessage ?? ''}
        alertColor="error"
      />
    </>
  );
}

function getCounterNumber(isSelectedItem: boolean, itemList: NavigationItem[]): string {
  if (!itemList.length) return '';

  if (!isSelectedItem) return itemList.length.toString();

  return itemList.length > 1 ? (itemList.length - 1).toString() : '';
}
